import { Typography, Grid, Box } from '@material-ui/core';
import { InfoCard, Page, Content } from '@backstage/core-components';
import { useTheme } from '@material-ui/core/styles';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { WidgetWrapper } from '../CommonComponent/WidgetWrapper';
import { useScorecard } from './ScoreCardContext';

import { ScoreCardProvider } from './ScoreCardContext'; // adjust path

ChartJS.register(ArcElement, Tooltip);

type ScorecardWidgetProps = {
  label: string;
  score: number;
  date: string;
  checks: number;
  color: string;
  minResponses?: number;
  checkLabel?: string;
};

const UnifiedScoreWidget = ({
  label,
  score,
  date,
  checks,
  color,
  minResponses = 0,
  checkLabel = 'Checks',
}: ScorecardWidgetProps) => {
  const theme = useTheme();
  const hasMinResponses = checks >= minResponses;
  const clampedScore = checks >= minResponses ? Math.min(Math.abs(score), 100) : 0;
  const remaining = 100 - clampedScore;

  const data = {
    labels: [label],
    datasets: [
      {
        data: [clampedScore, remaining],
        backgroundColor: [color, theme.palette.divider],
        borderWidth: 1,
      },
    ],
  };

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={3} container justifyContent="center">
        
          <Box width={100} height={100}>
            <Doughnut
              data={data}
              options={{
                cutout: '70%',
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: false },
                },
              }}
              plugins={[
                {
                  id: 'centerText',
                  afterDraw(chart) {
                    const {
                      ctx,
                      chartArea: { width, height },
                    } = chart;
                    ctx.save();
                    const text = hasMinResponses
                    ? `${clampedScore.toFixed(1)}%`
                    : '0%';
                    ctx.font = 'bold 16px Arial';
                    ctx.fillStyle = theme.palette.text.primary;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, width / 2, height / 2);
                    ctx.restore();
                  },
                },
              ]}
            />
          </Box>
       
      </Grid>

      <Grid item xs={6}>
        <Typography
          variant="subtitle1"
          style={{ fontSize: '0.9rem', color: theme.palette.text.secondary }}
        >
          {label}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          {date && !isNaN(new Date(date).getTime())
            ? `Last Run: ${new Date(date).toLocaleString()}`
            : ''}
        </Typography>
      </Grid>

      <Grid item xs={3} container direction="column" alignItems="center">
        <Typography variant="h6" color="primary">
          {checks}
        </Typography>
        <Typography variant="caption" style={{ fontSize: 12, opacity: 0.7 }}>
          {checkLabel}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const ScorecardComponentInner = () => {
  const { security, nps, sonar } = useScorecard();

  const getNpsColor = () => {
    if (nps.score > 0) return '#7BECDC';
    if (nps.score < 0) return '#D30F4B';
    return '#00BDFF';
  };

    let securityLabel = '';
if (security.error) {
  securityLabel = security.error;
}  else {
  securityLabel =
    'This score reflects the current security posture of the component. It is calculated based on checks like vulnerabilities';
}

  let securityScore = 0;
if (security.error) {
  securityScore = 0;
} else {
  securityScore = security.score;
}

  let sonarLabel = '';
if (sonar.error) {
  sonarLabel = sonar.error;
} else if (sonar.projectKeyMissing) {
  sonarLabel = 'Project key is not set for this repo';
} else {
  sonarLabel =
    'This score reflects the SonarQube posture of the component. It is calculated based on checks like Code Smells, Duplications, and other quality metrics.';
}

  let sonarScore = 0;
if (sonar.error) {
  sonarScore = 0;
} else if (sonar.projectKeyMissing) {
  sonarScore = 0;
} else {
  sonarScore = sonar.score;
}

  return (
    <Page themeId="tool">
      <Content>
        <Grid container spacing={3} direction="column">
          <Grid item>
            <InfoCard title="Score card" variant="gridItem">
              <WidgetWrapper
                title="Security Score"
                loading={security.loading}
                error=""
              >
                <UnifiedScoreWidget
                  label={securityLabel}
                  score={securityScore}
                  date={security.date}
                  checks={security.checks}
                  color="#7BECDC"
                  checkLabel="Checks"
                />
              </WidgetWrapper>
              <br />
              <WidgetWrapper
                title="NPS Score"
                loading={nps.loading}
                error={nps.error}
              >
                <UnifiedScoreWidget
                  label="The Net Promoter Score (NPS) captures user satisfaction with the component. It is derived from user ratings on a 1–5 scale, highlighting promoters and detractors."
                  score={nps.score}
                  date={nps.date}
                  checks={nps.responses}
                  color={getNpsColor()}
                  minResponses={5}
                  checkLabel="# Responders"
                />
              </WidgetWrapper>
              <br />
              <WidgetWrapper
                title="Code Quality Score (SonarQube)"
                loading={sonar.loading}
                error=""
              >
                <UnifiedScoreWidget
                  label={sonarLabel}
                  score={sonarScore}
                  date={sonar.date}
                  checks={sonar.checks}
                  color="#7BECDC"
                  checkLabel="Checks"
                />
              </WidgetWrapper>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const ScorecardComponent = () => (
  <ScoreCardProvider>
    <ScorecardComponentInner />
  </ScoreCardProvider>
);
